import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import { useProfessionals } from '../../../hooks/useProfessionals';
import { usePatientTerms } from './hooks/usePatientTerms';

function TermsTabList() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedTermTemplates, setSelectedTermTemplates] = useState('');
  const [selectedDentistId, setSelectedDentistId] = useState('');

  const {
    terms: termTemplates,
    error: termsError,
    generateTerm: generateTermRequest,
  } = usePatientTerms();
  const { professionals: dentists } = useProfessionals();

  useEffect(() => {
    if (termsError) {
      setShowErrorMessage(true);
      setErrorMessage(termsError);
    }
  }, [termsError]);

  const generateTerm = () => {
    if (!selectedTermTemplates) {
      setErrorMessage('Selecione um modelo de termo.');
      setShowErrorMessage(true);
      return;
    }

    if (!selectedDentistId) {
      setErrorMessage('Selecione um profissional.');
      setShowErrorMessage(true);
      return;
    }

    setLoading(true);

    generateTermRequest(patientId, selectedTermTemplates, selectedDentistId)
      .then((returnFromApi) => {
        setLoading(false);
        const url = window.URL.createObjectURL(returnFromApi.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'documento.docx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((returnFromApi) => {
        console.error('ops! ocorreu um erro' + returnFromApi);
        setLoading(false);
        if (returnFromApi.status === 500) {
          setShowErrorMessage(true);
          setErrorMessage('Ocorreu um erro interno no sistema, por favor contate o administrador');
        }
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Loading isToHidden={isLoading === false} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Paper sx={{ padding: 2, mb: 3 }} variant="outlined">
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Gerar Novo Termo
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl fullWidth sx={{ flex: 1, minWidth: '200px' }}>
            <InputLabel id="select-termo-template-label">Selecione o modelo</InputLabel>
            <Select
              labelId="select-termo-template-label"
              id="select-termo-template"
              value={selectedTermTemplates}
              label="Selecione o modelo"
              onChange={(e) => setSelectedTermTemplates(e.target.value)}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {termTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ flex: 1, minWidth: '200px' }}>
            <InputLabel id="dentist-select-label">Selecione o Profissional</InputLabel>
            <Select
              labelId="dentist-select-label"
              id="dentist-select"
              value={selectedDentistId}
              label="Selecione o Profissional"
              onChange={(e) => setSelectedDentistId(e.target.value)}
            >
              <MenuItem value="">
                <em>Selecione...</em>
              </MenuItem>
              {dentists.length === 0 && (
                <MenuItem disabled value="">
                  Nenhum profissional encontrado
                </MenuItem>
              )}
              {dentists.map((dentist) => (
                <MenuItem key={dentist.id} value={dentist.id}>
                  {dentist.nome || dentist.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={generateTerm}
            disabled={!selectedTermTemplates || !selectedDentistId || isLoading}
          >
            Gerar Termo
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default TermsTabList;
